/**
 * Phaser-free checkpoint look: dashed-gate and checkered-goal geometry, 2.5-D pole offsets, and
 * the order badge placement both scenes share. Colours and world sizes live here so play and the
 * editor cannot drift, and so the dash / checker walks can be unit-tested without a renderer.
 */

import { CONTACT_BLOB_FLATTEN } from './contact.ts';
import { closestPointOnSegment, distance, midpoint } from './document.ts';
import type { Checkpoint, Vec2 } from './types.ts';

export const GATE_LINE_COLOR = 0xffffff;
export const GATE_LINE_ALPHA = 0.5;
export const GATE_LINE_SELECTED_ALPHA = 0.78;
export const GATE_LINE_WIDTH_PX = 2;
export const GATE_DASH_PX = 7;
export const GATE_GAP_PX = 5;
/** Side of one checker square at zoom 1. Two rows make the ribbon. */
export const GATE_CHECKER_SIZE_PX = 8;
export const GATE_CHECKER_ROWS = 2;
/** Two rows of squares — thicker than the ordinary 2 px dash. */
export const GATE_CHECKER_WIDTH_PX = GATE_CHECKER_SIZE_PX * GATE_CHECKER_ROWS;
/** Screen scale follows zoom, then stays in this band so pull-back and zoom-in stay readable. */
export const GATE_CHECKER_ZOOM_MIN = 0.75;
export const GATE_CHECKER_ZOOM_MAX = 1.75;
export const GATE_CHECKER_LIGHT = 0xffffff;
export const GATE_CHECKER_DARK = 0x111111;
export const GATE_CHECKER_ALPHA = 0.4;
export const GATE_CHECKER_SELECTED_ALPHA = 0.58;

/** Zoom used to size the ribbon: follows the camera, clamped to `0.75`–`1.75`. */
export function checkerZoomFactor(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) {
    return 1;
  }

  return Math.min(GATE_CHECKER_ZOOM_MAX, Math.max(GATE_CHECKER_ZOOM_MIN, zoom));
}

/** World units per authored square pixel at this camera zoom. */
export function checkerWorldScale(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) {
    return 1;
  }

  return checkerZoomFactor(zoom) / zoom;
}

export const POLE_PENDING_COLOR = 0xe24a4a;
export const POLE_PASSED_COLOR = 0x2fbe6a;
/** Hover preview: the planned move line crosses this still-pending gate. */
export const POLE_PREVIEW_COLOR = 0xe0b64f;
/** Same cream stroke the editor uses on a selected island. */
export const POLE_SELECTED_STROKE = 0xf4f7ee;
export const POLE_SELECTED_STROKE_PX = 3;
/** Authored at zoom 1. Draw and hit-test multiply by `checkerWorldScale`. */
export const POLE_RADIUS_WORLD = 7;
/** Shaft length above the waterline, leaning screen-up (−Y / north). Authored at zoom 1. */
export const POLE_HEIGHT_WORLD = 30;
/** Faded stub below the waterline, leaning screen-down (+Y). Length stays world-fixed so land clip is zoom-independent; thickness follows `checkerWorldScale`. */
export const POLE_SUBMERGED_WORLD = 40;
/** Waterline ellipse height as a fraction of its width, so the base reads as a post, not a disc. */
export const POLE_WATERLINE_FLATTEN = CONTACT_BLOB_FLATTEN;
export const POLE_STUB_STEPS = 8;
/** Slices used to paint the fade so it reads as a gradient, not a few flat steps. */
export const POLE_STUB_FADE_SLICES = 16;
/** World-unit step used to clip a gate line to open water. */
export const WATER_CLIP_STEP = 4;

export const BADGE_RADIUS_PX = 10;
export const BADGE_OFFSET_PX = 16;
export const BADGE_FILL_COLOR = 0xffffff;
export const BADGE_FILL_ALPHA = 0.28;
export const BADGE_STROKE_ALPHA = 0.55;
export const BADGE_GOAL_STROKE = 0xf0d78c;
export const BADGE_TEXT_COLOR = '#0b1d2a';

export const DEGENERATE_GATE_WIDTH = 1e-6;

export type DashSegment = {
  a: Vec2;
  b: Vec2;
};

export type CheckerCell = {
  a0: Vec2;
  a1: Vec2;
  b1: Vec2;
  b0: Vec2;
  dark: boolean;
};

/** Walk `from` → `to` emitting dash strokes, then skipping `gap`. Degenerate spans yield nothing. */
export function dashedSegments(from: Vec2, to: Vec2, dash: number, gap: number): DashSegment[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);

  if (length < DEGENERATE_GATE_WIDTH || dash <= 0) {
    return [];
  }

  const ux = dx / length;
  const uy = dy / length;
  const period = dash + Math.max(0, gap);
  const segments: DashSegment[] = [];
  let travelled = 0;

  while (travelled < length) {
    const end = Math.min(travelled + dash, length);
    segments.push({
      a: { x: from.x + ux * travelled, y: from.y + uy * travelled },
      b: { x: from.x + ux * end, y: from.y + uy * end },
    });
    travelled += period;
  }

  return segments;
}

/**
 * Walk `from` → `to` as a two-row checkerboard ribbon. `cell` is the along-span square size;
 * `halfWidth` is half the total ribbon width. The last column clips to the span end. Degenerate
 * spans, a non-positive cell, or a non-positive half-width yield nothing.
 */
export function checkeredCells(
  from: Vec2,
  to: Vec2,
  cell: number,
  halfWidth: number,
): CheckerCell[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);

  if (length < DEGENERATE_GATE_WIDTH || cell <= 0 || halfWidth <= 0) {
    return [];
  }

  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;
  const cells: CheckerCell[] = [];
  const band = halfWidth * 2;
  let travelled = 0;
  let col = 0;

  while (travelled < length) {
    const end = Math.min(travelled + cell, length);

    for (let row = 0; row < GATE_CHECKER_ROWS; row += 1) {
      const r0 = -halfWidth + (row / GATE_CHECKER_ROWS) * band;
      const r1 = -halfWidth + ((row + 1) / GATE_CHECKER_ROWS) * band;
      cells.push({
        a0: { x: from.x + ux * travelled + nx * r0, y: from.y + uy * travelled + ny * r0 },
        a1: { x: from.x + ux * travelled + nx * r1, y: from.y + uy * travelled + ny * r1 },
        b1: { x: from.x + ux * end + nx * r1, y: from.y + uy * end + ny * r1 },
        b0: { x: from.x + ux * end + nx * r0, y: from.y + uy * end + ny * r0 },
        dark: (col + row) % 2 === 1,
      });
    }

    travelled = end;
    col += 1;
  }

  return cells;
}

/**
 * Dash a polyline with one period so corners do not restart the pattern.
 * `closed` walks the last vertex back to the first.
 */
export function dashedPolyline(
  points: readonly Vec2[],
  closed: boolean,
  dash: number,
  gap: number,
): DashSegment[] {
  if (points.length < 2 || dash <= 0) {
    return [];
  }

  const last = points.length - 1;
  const edgeCount = closed && points.length >= 3 ? points.length : last;
  const period = dash + Math.max(0, gap);
  const segments: DashSegment[] = [];
  let phase = 0;

  for (let i = 0; i < edgeCount; i += 1) {
    const from = points[i];
    const to = points[i === last ? 0 : i + 1];

    if (!from || !to) {
      continue;
    }

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);

    if (length < DEGENERATE_GATE_WIDTH) {
      continue;
    }

    const ux = dx / length;
    const uy = dy / length;
    let travelled = 0;

    while (travelled < length) {
      const inDash = phase < dash;
      const remain = inDash ? dash - phase : period - phase;
      const take = Math.min(remain, length - travelled);

      if (inDash && take > 0) {
        segments.push({
          a: { x: from.x + ux * travelled, y: from.y + uy * travelled },
          b: { x: from.x + ux * (travelled + take), y: from.y + uy * (travelled + take) },
        });
      }

      travelled += take;
      phase += take;

      if (phase >= period) {
        phase -= period;
      }
    }
  }

  return segments;
}

/** World multiplier for pole radius and height. Same clamp as the checkered ribbon. */
export function poleWorldScale(zoom: number): number {
  return checkerWorldScale(zoom);
}

function sanePoleScale(scale: number): number {
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

export function poleCap(base: Vec2, scale = 1): Vec2 {
  return { x: base.x, y: base.y - POLE_HEIGHT_WORLD * sanePoleScale(scale) };
}

/** True when `point` is within `radius` of the above-water shaft (base → cap). */
export function pointHitsPole(
  base: Vec2,
  point: Vec2,
  radius = POLE_RADIUS_WORLD,
  scale = 1,
): boolean {
  return distance(point, closestPointOnSegment(point, base, poleCap(base, scale))) <= radius;
}

/** Axis-aligned box around the above-water pole, used for marquee hits. */
export function poleBounds(
  base: Vec2,
  scale = 1,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const cap = poleCap(base, scale);
  const radius = POLE_RADIUS_WORLD * sanePoleScale(scale);
  return {
    minX: base.x - radius,
    minY: cap.y - radius,
    maxX: base.x + radius,
    maxY: base.y + radius,
  };
}

export function poleIntersectsRect(
  base: Vec2,
  rect: { minX: number; minY: number; maxX: number; maxY: number },
  scale = 1,
): boolean {
  const bounds = poleBounds(base, scale);
  return !(
    bounds.maxX < rect.minX ||
    bounds.minX > rect.maxX ||
    bounds.maxY < rect.minY ||
    bounds.minY > rect.maxY
  );
}

export function poleStubTip(base: Vec2): Vec2 {
  return { x: base.x, y: base.y + POLE_SUBMERGED_WORLD };
}

/** Underwater alpha along the stub: 1 at the waterline, 0 at the tip. */
export function poleStubFade(t: number): number {
  return 1 - Math.min(1, Math.max(0, t));
}

export type PoleStubSample = {
  point: Vec2;
  t: number;
};

/** Sample points along the faded stub, from just below the waterline to the tip. */
export function poleStubSamples(base: Vec2): PoleStubSample[] {
  const samples: PoleStubSample[] = [];

  for (let step = 1; step <= POLE_STUB_STEPS; step += 1) {
    const t = step / POLE_STUB_STEPS;
    samples.push({ point: { x: base.x, y: base.y + POLE_SUBMERGED_WORLD * t }, t });
  }

  return samples;
}

/**
 * Stub samples that sit in open water. A pole planted on land has no underwater part; a stub that
 * later hits ground or rock stops there and does not poke out the far side.
 */
export function poleStubVisibleSamples(base: Vec2, isLand: (point: Vec2) => boolean): PoleStubSample[] {
  if (isLand(base)) {
    return [];
  }

  const visible: PoleStubSample[] = [];

  for (const sample of poleStubSamples(base)) {
    if (isLand(sample.point)) {
      break;
    }

    visible.push(sample);
  }

  return visible;
}

/** Water-side of the land/water crossing between `waterT` and `landT`, so dashes never paint land. */
function shoreline(
  at: (distanceAlong: number) => Vec2,
  waterT: number,
  landT: number,
  isLand: (point: Vec2) => boolean,
): number {
  let water = waterT;
  let land = landT;

  for (let step = 0; step < 8; step += 1) {
    const mid = (water + land) / 2;

    if (isLand(at(mid))) {
      land = mid;
    } else {
      water = mid;
    }
  }

  return water;
}

/**
 * Splits `from` → `to` into the runs that sit on water. `isLand` is a predicate so this stays
 * Phaser-free; play and the editor pass `pointInSolid`.
 */
export function waterSpans(from: Vec2, to: Vec2, isLand: (point: Vec2) => boolean, step = WATER_CLIP_STEP): DashSegment[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);

  if (length < DEGENERATE_GATE_WIDTH) {
    return [];
  }

  const ux = dx / length;
  const uy = dy / length;
  const at = (distanceAlong: number): Vec2 => ({
    x: from.x + ux * distanceAlong,
    y: from.y + uy * distanceAlong,
  });

  const spans: DashSegment[] = [];
  let spanStart = isLand(from) ? null : 0;
  let travelled = 0;

  while (travelled < length) {
    const next = Math.min(travelled + Math.max(step, 1), length);
    const land = isLand(at(next));

    if (spanStart === null && !land) {
      spanStart = shoreline(at, next, travelled, isLand);
    } else if (spanStart !== null && land) {
      const end = shoreline(at, travelled, next, isLand);

      if (end - spanStart > DEGENERATE_GATE_WIDTH) {
        spans.push({ a: at(spanStart), b: at(end) });
      }

      spanStart = null;
    }

    travelled = next;
  }

  if (spanStart !== null && length - spanStart > DEGENERATE_GATE_WIDTH) {
    spans.push({ a: at(spanStart), b: to });
  }

  return spans;
}

/**
 * The inspector Order value, or null when Order is 0 (unordered). The badge circle is only drawn
 * for a numbered gate.
 */
export function checkpointDisplayOrder(checkpoint: Checkpoint): number | null {
  return checkpoint.order !== null && checkpoint.order > 0 ? checkpoint.order : null;
}

function distanceToPole(point: Vec2, base: Vec2, scale: number): number {
  return distance(point, closestPointOnSegment(point, base, poleCap(base, scale)));
}

function badgeMinDistance(strokeScale: number, poleScale: number): number {
  return (BADGE_RADIUS_PX + BADGE_OFFSET_PX) * strokeScale + POLE_RADIUS_WORLD * poleScale;
}

function slideOffPoles(
  origin: Vec2,
  dir: Vec2,
  minDist: number,
  a: Vec2,
  b: Vec2,
  poleScale: number,
): Vec2 {
  let offset = minDist;

  for (let step = 0; step < 32; step += 1) {
    const point = { x: origin.x + dir.x * offset, y: origin.y + dir.y * offset };

    if (
      distanceToPole(point, a, poleScale) >= minDist &&
      distanceToPole(point, b, poleScale) >= minDist
    ) {
      return point;
    }

    offset += minDist * 0.25;
  }

  return { x: origin.x + dir.x * offset, y: origin.y + dir.y * offset };
}

/**
 * Order badge sits beside the gate, offset along the perpendicular so it never covers a pole.
 * A point gate (both flags on one spot) puts the badge beside the shaft, not on the cap.
 */
export function checkpointBadgePosition(a: Vec2, b: Vec2, strokeScale: number): Vec2 {
  const poleScale = poleWorldScale(strokeScale > 0 ? 1 / strokeScale : 1);
  const minDist = badgeMinDistance(strokeScale, poleScale);
  const center = midpoint(a, b);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);

  if (length < DEGENERATE_GATE_WIDTH) {
    return { x: a.x + minDist, y: (a.y + poleCap(a, poleScale).y) / 2 };
  }

  const nx = -dy / length;
  const ny = dx / length;
  const first = slideOffPoles(center, { x: nx, y: ny }, minDist, a, b, poleScale);
  const second = slideOffPoles(center, { x: -nx, y: -ny }, minDist, a, b, poleScale);
  const score = (point: Vec2): number =>
    Math.min(distanceToPole(point, a, poleScale), distanceToPole(point, b, poleScale));
  return score(first) >= score(second) ? first : second;
}
